import { createHttpLink, from } from "@apollo/client/core";
import { onError } from "@apollo/client/link/error";
import * as Sentry from "@sentry/nuxt";

export default defineNuxtPlugin((nuxtApp) => {
  const { $apollo } = nuxtApp;
  const CMS_DOMAIN = nuxtApp.$config?.public?.CMS_DOMAIN;
  const URL_LENGTH_THRESHOLD =
    nuxtApp.$config?.public?.URL_LENGTH_THRESHOLD || 2048;

  // Use Nuxt's built-in import.meta.dev instead of NODE_ENV for reliability
  const IS_DEV = import.meta.dev;
  const IS_PRODUCTION = !import.meta.dev;

  if (!CMS_DOMAIN) {
    throw new Error("CMS_DOMAIN is not configured in nuxt.config");
  }

  // trigger the error hook on an error
  const errorLink = onError((err) => {
    nuxtApp.callHook("apollo:error", err);
  });

  /**
   * Extract URL metadata for logging and debugging
   * @param {URL} url - The URL object to analyze
   * @returns {Object} URL metadata
   */
  const getUrlMetadata = (url) => {
    const queryParam = url.searchParams.get("query");
    return {
      urlLength: url.toString().length,
      queryLength: queryParam?.length || 0,
      url: url.toString(),
      query: queryParam,
    };
  };

  /**
   * Retry failed GET request as POST request
   * @param {URL} url - The original URL object
   * @param {RequestInit} options - The original fetch options
   * @returns {Promise<Response>} The POST request response
   */
  const retryAsPostRequest = async (url, options) => {
    // Preserve token parameter in URL (for preview functionality)
    const tokenParam = url.searchParams.get("token");
    const baseUrl = tokenParam
      ? `${url.origin}${url.pathname}?token=${tokenParam}`
      : `${url.origin}${url.pathname}`;

    // Extract GraphQL query parameters for POST body
    const postBody = {};

    // Copy all query parameters to POST body except 'token' (keep it in URL)
    url.searchParams.forEach((value, key) => {
      if (key === "token") return; // Skip token, it's in the URL

      // GraphQL query should remain as string
      // Variables, extensions, and other params should be parsed as JSON
      if (key === "query" || key === "operationName") {
        postBody[key] = value;
      } else {
        try {
          // Try to parse JSON values (like variables object)
          postBody[key] = JSON.parse(value);
        } catch {
          // If not JSON, use as-is
          postBody[key] = value;
        }
      }
    });

    const postOptions = {
      ...options,
      method: "POST",
      headers: {
        ...options?.headers,
        "content-type": "application/json",
      },
      body: JSON.stringify(postBody),
      _isRetryAttempt: true, // Mark this as a retry attempt to prevent infinite loops
    };

    try {
      const retryResult = await fetch(baseUrl, postOptions);

      if (!retryResult.ok) {
        if (import.meta.dev) {
          console.error("POST retry failed with status:", retryResult.status);
        }

        // Clone response to read body for logging without consuming it
        const clone = retryResult.clone();
        const errorBody = await clone.text();

        Sentry.captureException(
          new Error("GraphQL POST Retry Failed After 414"),
          {
            level: "error",
            tags: {
              errorType: "post_retry_failed",
              apolloClient: "craft_cms",
              statusCode: retryResult.status,
            },
            extra: {
              url: baseUrl,
              responseStatus: retryResult.status,
              errorBody: errorBody.substring(0, 1000),
              // Don't log full postBody - may contain sensitive data
              postBodyKeys: Object.keys(postBody),
            },
          },
        );
      }

      return retryResult;
    } catch (error) {
      if (import.meta.dev) {
        console.error("POST retry threw error:", error);
      }

      Sentry.captureException(error, {
        level: "error",
        tags: {
          errorType: "post_retry_exception",
          apolloClient: "craft_cms",
        },
        extra: {
          url: baseUrl,
          originalError: error.message,
          // Don't log full postBody - may contain sensitive data
          postBodyKeys: Object.keys(postBody),
        },
      });

      throw error;
    }
  };

  // Custom fetch that normalizes query strings by removing excessive spaces
  const customFetch = async (uri, options) => {
    const url = new URL(uri);

    // Check if this is already a retry attempt to prevent infinite loops
    const isRetryAttempt = options?._isRetryAttempt === true;

    try {
      // If there's a query parameter, normalize it by collapsing multiple spaces
      if (url.searchParams.has("query")) {
        const query = url.searchParams.get("query");
        const normalizedQuery = query.replace(/\s+/g, " ").trim();
        url.searchParams.set("query", normalizedQuery);
      }

      const response = await fetch(url.toString(), options);

      // Check for 414 Request URI Too Long error
      if (response.status === 414) {
        // Don't retry if this was already a retry attempt
        if (isRetryAttempt) {
          if (import.meta.dev) {
            console.error(
              "POST retry also returned 414, stopping to prevent infinite loop",
            );
          }

          const metadata = getUrlMetadata(url);

          Sentry.captureException(
            new Error("GraphQL POST Retry Also Failed With 414"),
            {
              level: "error",
              tags: {
                errorType: "414_retry_loop_prevented",
                apolloClient: "craft_cms",
              },
              extra: {
                urlLength: metadata.urlLength,
                queryLength: metadata.queryLength,
              },
            },
          );

          throw new Error("Request failed with 414 even after POST retry");
        }

        const metadata = getUrlMetadata(url);

        Sentry.captureException(
          new Error("GraphQL GET Request URI Too Long (414)"),
          {
            level: "warning",
            tags: {
              errorType: "414_request_uri_too_long",
              apolloClient: "craft_cms",
              fallback: "post_retry",
            },
            extra: {
              ...metadata,
              headers: options?.headers || {},
            },
          },
        );

        // Fallback: Retry as POST request
        return await retryAsPostRequest(url, options);
      }

      return response;
    } catch (error) {
      // Don't retry if this was already a retry attempt
      if (isRetryAttempt) {
        if (IS_DEV) {
          console.error(
            "Retry attempt failed, stopping to prevent infinite loop",
          );
        }

        if (IS_PRODUCTION) {
          Sentry.captureException(error, {
            level: "error",
            tags: {
              errorType: "retry_loop_prevented",
              apolloClient: "craft_cms",
            },
            extra: {
              url: url.toString().substring(0, 500),
              originalError: error.message,
            },
          });
        }

        throw error;
      }

      const metadata = getUrlMetadata(url);

      // Only retry if URL seems problematically long (likely 414 error)
      // Conservative estimate based on typical nginx/apache limits
      if (metadata.urlLength > URL_LENGTH_THRESHOLD) {
        if (IS_DEV) {
          console.log("Network error with long URL, retrying as POST");
        }

        if (IS_PRODUCTION) {
          Sentry.captureException(
            new Error("GraphQL GET Request Failed (likely 414)"),
            {
              level: "warning",
              tags: {
                errorType: "414_network_error",
                apolloClient: "craft_cms",
                fallback: "post_retry",
              },
              extra: {
                urlLength: metadata.urlLength,
                queryLength: metadata.queryLength,
                originalError: error.message,
              },
            },
          );
        }

        return await retryAsPostRequest(url, options);
      }

      // For other network errors, log and rethrow
      if (IS_DEV) {
        console.error("Network error (not 414-related):", error);
      }

      if (IS_PRODUCTION) {
        Sentry.captureException(error, {
          level: "error",
          tags: {
            errorType: "network_error",
            apolloClient: "craft_cms",
          },
          extra: {
            urlLength: metadata.urlLength,
            originalError: error.message,
          },
        });
      }

      throw error;
    }
  };

  // Create httpLink with conditional uri based on client/server
  const httpLink = createHttpLink({
    useGETForQueries: true,
    fetch: customFetch,
    uri: () => {
      let token = null;

      // Only check for preview token on client side
      if (import.meta.client) {
        const previewToken = useState("previewToken", () => null);
        token =
          // eslint-disable-next-line
          new URLSearchParams(window?.location?.search)?.get?.("token") ??
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          previewToken.value;
      }

      return `${CMS_DOMAIN}/api${token ? `?token=${token}` : ""}`;
    },
  });

  const composedLink = from([errorLink, httpLink]);
  $apollo.defaultClient.setLink(composedLink);

  // Cleanup on app unmount to prevent memory leaks
  if (import.meta.client) {
    nuxtApp.hook("app:unmounted", () => {
      // Apollo client cleanup will be handled by the Apollo plugin itself
      // Just ensure we're not holding any references
      if ($apollo.defaultClient) {
        $apollo.defaultClient.stop();
      }
    });
  }

  // SSR cleanup: stop the per-request ApolloClient after the page is rendered.
  // Without this, ObservableQuery subscriptions keep the client alive via async
  // context (Promise's kResourceStore), which transitively retains nuxtApp,
  // AppContext, every component instance, and their reactive scopes — the
  // pattern responsible for the long-running RSS growth on production.
  // Runs after @nuxtjs/apollo's own app:rendered hook (which extracts the
  // cache into the payload), so SSR data is already serialized.
  if (import.meta.server) {
    nuxtApp.hook("app:rendered", () => {
      if ($apollo.defaultClient) {
        $apollo.defaultClient.stop();
      }
    });
  }
});
